import React, { useState } from "react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useAuth } from "../../../firebase/AuthProvider";
import { HolidayUpsertRequest } from "../../../interfaces";
import { SaveHolidayAsync } from "../../../services/HolidaysService/HolidaysService";

type AddHolidayFormProps = {
    onSuccess?: () => void;
};

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "13px 16px",
    border: "1px solid #0000001A",
    background: "transparent",
    outline: "none",
    boxShadow: "none",
    color: "#717182",
    fontWeight: "400",
    borderRadius: "10px",
};

const inputClass =
    "!text-[13px] 2xl:!text-[16px] !font-normal";

const AddHolidayForm: React.FC<AddHolidayFormProps> = ({ onSuccess }) => {
    const { currentUser } = useAuth();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    // ---------- Helpers ----------
    const todayDateString = () => {
        const d = new Date();
        return d.toISOString().split("T")[0];
    };

    const clearError = (field: string) => {
        setErrors((prev) => {
            const copy = { ...prev };
            delete copy[field];
            return copy;
        });
    };

    const formatLocalDateTime = (dateStr: string, endOfDay = false) => {
        const d = new Date(dateStr);
        if (endOfDay) d.setHours(23, 59, 59, 999);
        else d.setHours(0, 0, 0, 0);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const min = String(d.getMinutes()).padStart(2, "0");
        const ss = String(d.getSeconds()).padStart(2, "0");
        const ms = String(d.getMilliseconds()).padStart(3, "0");
        return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}.${ms}`; // local datetime, no 'Z'
    };

    const validate = () => {
        const e: Record<string, string> = {};
        const today = todayDateString();

        if (!title.trim()) e.title = "Holiday name is required";
        if (!startDate) e.startDate = "Start date is required";
        if (!endDate) e.endDate = "End date is required";

        if (startDate && startDate < today)
            e.startDate = "Start date cannot be before today";

        if (endDate && endDate < today)
            e.endDate = "End date cannot be before today";

        if (startDate && endDate && startDate > endDate)
            e.endDate = "End date must be after start date";

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ---------- Submit ----------
    const handleSubmit = async () => {
        if (!validate()) return;

        const token = await currentUser?.getIdToken();
        if (!token) {
            toast.error("Authentication error");
            return;
        }

        setLoading(true);
        try {
            const payload: HolidayUpsertRequest = {
                title: title.trim(),
                description: description.trim(),
                startDate: formatLocalDateTime(startDate),
                endDate: formatLocalDateTime(endDate, true),
            };

            const res = await SaveHolidayAsync(token, payload);

            if (res.success) {
                toast.success("Holiday added successfully");
                setTitle("");
                setDescription("");
                setStartDate("");
                setEndDate("");
                setErrors({});
                onSuccess?.();
            } else {
                toast.error(res.message || "Failed to add holiday");
            }
        } catch (err) {
            toast.error(
                err instanceof AxiosError
                    ? err.response?.data?.message
                    : "Something went wrong"
            );
        } finally {
            setLoading(false);
        }
    };

    const todayStr = todayDateString();

    // ---------- UI ----------
    return (
        <div className="space-y-4 p-6">
            {/* Holiday Name */}
            <div>
                <p className="mb-1 text-[14px] text-gray-800">Holiday Name</p>
                <input
                    placeholder="Name here.."
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        clearError("title");
                    }}
                    className={inputClass}
                    style={inputStyle}
                />
                {errors.title && (
                    <p className="text-red-500 text-xs mt-1">{errors.title}</p>
                )}
            </div>

            <div>
                <p className="mb-1 text-[14px] text-gray-800">Start Date</p>
                <input
                    type="date"
                    placeholder="startDate"
                    value={startDate}
                    min={todayStr}
                    onChange={(e) => {
                        setStartDate(e.target.value);
                        clearError("startDate");
                        // clearError("endDate"); // important if end date depends on it
                    }}
                    className={inputClass}
                    style={inputStyle}
                />
                {errors.startDate && (
                    <p className="text-red-500 text-xs mt-1">
                        {errors.startDate}
                    </p>
                )}
            </div>

            <div className="mb-5">
                <p className="mb-1  text-[14px] text-gray-800">End Date</p>
                <input
                    type="date"
                    placeholder="endDate"
                    value={endDate}
                    min={startDate || todayStr}
                    onChange={(e) => {
                        setEndDate(e.target.value);
                        clearError("endDate");
                    }}
                    className={inputClass}
                    style={inputStyle}
                />
                {errors.endDate && (
                    <p className="text-red-500 text-xs mt-1">
                        {errors.endDate}
                    </p>
                )}
            </div>

            {/* <div>
                <textarea
                    placeholder="Description (optional)"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className={inputClass}
                    style={inputStyle}
                />
            </div> */}

            {/* Submit */}
            <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-[#7E57C2] text-white py-3 rounded-[10px] hover:bg-[#6c46ad] disabled:opacity-50"
            >
                {loading ? "Saving..." : "Add Holiday"}
            </button>
        </div>
    );
};

export default AddHolidayForm;
